// EntryDetail (job page round 2, Amir 26 Oct points 8 and 12: "every history item clickable to its
// detail, one consistent detail pattern with per-kind content"): anything on the job record opened in
// full, in the wide side panel over the page. One pattern for every kind (a visit, a call, a text, a
// note, a payment, a form, an estimate, a change, a booking, photos, parts, a signature, the invoice,
// a task, the imported summary): its first line the thing itself, a muted line saying who and when, one
// red line only if something's wrong; then its sections (facts in KeyValue, words as they were written,
// a table, photos), one main button and the rest as links; then Earlier and Later, which step through
// History in the order it was opened from. A link inside to another detail replaces this one, and
// "‹ Back to …" comes back. Content only: SidePanel draws the layer, its Close and its focus.
// Inline (History, opened in place: Amir's pick of 26 Oct, pages/job-history-option): only its red
// line, its sections and its links, under the row that is its title; no panel, no title or sub, no
// back, no Earlier and Later (the rows before and after are right there). Its main button is drawn as
// the first link, so several open entries never put several filled buttons on the page.
// Reads only --entry-*; SidePanel, KeyValue, LineTable, PhotoThumb and Link read their own.
import { KeyValue } from '../../components/KeyValue/KeyValue.tsx';
import { LineTable } from '../../components/LineTable/LineTable.tsx';
import { MiniInvoice } from '../../components/MiniInvoice/MiniInvoice.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { PhotoThumb } from '../../components/PhotoThumb/PhotoThumb.tsx';
import { SidePanel } from '../../components/SidePanel/SidePanel.tsx';
import type { ActionLink } from '../job-view.ts';
import type { RecordDetail } from '../record-view.ts';
import './EntryDetail.css';

export interface EntryDetailProps {
  detail: RecordDetail;
  onClose: () => void;
  /** Another detail (Earlier, Later, a link to one). */
  onGo: (id: string) => void;
  /** Any other link or the main button. */
  onAction?: (a: ActionLink) => void;
  /** It replaced another detail: the way back. */
  back?: { label: string; id: string };
  /** The titles of Earlier and Later, for their words. */
  titleOf?: (id: string) => string | undefined;
  /** Storybook: the panel in the flow of the page, not fixed to the window. */
  isOpen?: boolean;
  /** In the wide side panel (the default), or in place under its History row. */
  placement?: 'panel' | 'inline';
}

export function EntryDetail({
  detail,
  onClose,
  onGo,
  onAction,
  back,
  titleOf,
  isOpen = true,
  placement = 'panel',
}: EntryDetailProps) {
  const press = (a: ActionLink) => () =>
    a.target?.startsWith('detail:') ? onGo(a.target.slice(7)) : onAction?.(a);
  if (placement === 'inline') {
    const links = [...(detail.primary ? [detail.primary] : []), ...(detail.links ?? [])];
    return (
      <div className="fl-entry" data-inline="">
        {detail.note ? <p className="fl-entry__note">{detail.note}</p> : null}
        <Sections detail={detail} />
        {links.length ? (
          <div className="fl-entry__links">
            {links.map((l) =>
              l.href ? (
                <Link key={`${l.id}-${l.target ?? ''}`} href={l.href}>
                  {l.label}
                </Link>
              ) : (
                <Link
                  key={`${l.id}-${l.target ?? ''}`}
                  variant={l.isDanger ? 'danger' : undefined}
                  onPress={press(l)}
                >
                  {l.label}
                </Link>
              ),
            )}
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <SidePanel
      label={`The detail: ${detail.label}`}
      title={detail.title}
      sub={detail.sub}
      note={detail.note}
      width="wide"
      takesFocus
      isOpen={isOpen}
      onClose={onClose}
      before={back ? <Link onPress={() => onGo(back.id)}>‹ Back to {back.label}</Link> : undefined}
      primary={detail.primary ? { label: detail.primary.label, onPress: press(detail.primary) } : null}
      links={(detail.links ?? []).map((l) => ({ label: l.label, href: l.href, onPress: press(l) }))}
      // Earlier and Later come last, after its actions
      after={
        detail.earlier || detail.later ? (
          <nav className="fl-entry__steps" aria-label="In the history">
            {detail.earlier ? (
              <Link onPress={() => onGo(detail.earlier!)}>
                ‹ Earlier{titleOf?.(detail.earlier) ? `: ${titleOf(detail.earlier)}` : ''}
              </Link>
            ) : (
              <span />
            )}
            {detail.later ? (
              <Link onPress={() => onGo(detail.later!)}>
                Later{titleOf?.(detail.later) ? `: ${titleOf(detail.later)}` : ''} ›
              </Link>
            ) : null}
          </nav>
        ) : null
      }
    >
      <div className="fl-entry">
        <Sections detail={detail} />
      </div>
    </SidePanel>
  );
}

/** Its sections: facts, words as written, a table, photos, each under its heading, rules between. */
function Sections({ detail }: { detail: RecordDetail }) {
  return (
    <>
      {detail.sections.map((s, i) => (
        <section key={i} className="fl-entry__section" aria-label={s.heading}>
          {s.heading ? <h3 className="fl-entry__heading">{s.heading}</h3> : null}
          {s.quote ? <blockquote className="fl-entry__quote">{s.quote}</blockquote> : null}
          {s.text ? <p className="fl-entry__text">{s.text}</p> : null}
          {s.facts?.length ? <KeyValue items={s.facts} density="tight" /> : null}
          {s.table ? (
            // a wide table (time on site) scrolls in its own row on a phone; the panel never does
            <div className="fl-entry__table">
              <LineTable
                label={s.table.label}
                columns={s.table.columns}
                rows={s.table.rows.map((r, j) => ({ id: String(j), cells: r }))}
              />
            </div>
          ) : null}
          {s.invoice ? <MiniInvoice invoice={s.invoice} /> : null}
          {s.photos?.length ? (
            <div className="fl-entry__photos">
              {s.photos.map((p) => (
                <PhotoThumb key={p.id} photo={p} />
              ))}
            </div>
          ) : null}
        </section>
      ))}
    </>
  );
}
